import axios, { AxiosRequestConfig, AxiosResponse, RawAxiosResponseHeaders, AxiosResponseHeaders } from "axios"
import { useAuthToken } from "@/composables/useAuthToken"

/**
 * Error object structure
 */
export interface ApiError {
  message: string
  field: string | null
}

/**
 * Standard API Response Interface
 */
export interface ApiResponse<T = unknown> {
  headers: RawAxiosResponseHeaders | AxiosResponseHeaders | null
  data: T
  success: boolean
  errors?: ApiError[]
  message?: string
}

export default class BaseApiService {
  private authToken: ReturnType<typeof useAuthToken>
  private static interceptorsConfigured = false

  constructor() {
    this.authToken = useAuthToken()
    this.setupAxiosInterceptors()
    this.initializeAuthentication()
    this.configureDefaultTimeout()
  }

  /**
   * Configure default timeout for all axios requests
   * M9: Prevent frontend timeouts for long-running operations like BluDelta analysis
   */
  private configureDefaultTimeout(): void {
    // Set default timeout to 45 seconds
    // This prevents hanging requests while allowing reasonable response times
    axios.defaults.timeout = 45000
  }

  /**
   * Initialize authentication with secure token handling
   */
  private initializeAuthentication(): void {
    const token = this.authToken.getToken()
    if (token && this.authToken.checkAuthentication()) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`
    } else {
      // Clear any existing authorization header if no valid token
      delete axios.defaults.headers.common["Authorization"]
    }
  }

  /**
   * Setup axios interceptors for automatic token handling and error management
   * Uses singleton pattern to prevent duplicate interceptor registration
   */
  private setupAxiosInterceptors(): void {
    if (BaseApiService.interceptorsConfigured) {
      return
    }
    BaseApiService.interceptorsConfigured = true

    // Request interceptor - ensure fresh token for each request
    axios.interceptors.request.use(
      (config) => {
        const token = this.authToken.getToken()
        if (token && this.authToken.checkAuthentication()) {
          config.headers["Authorization"] = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor - handle auth errors automatically
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // 401 only. Authentication failures always arrive as 401 (Yii's
        // CompositeAuth/HttpBearerAuth throw UnauthorizedHttpException); a 403
        // means "logged in, but not allowed here" and must not tear down the
        // session — that would throw the user out of the app over a permission
        // message they never get to read.
        if (error.response && error.response.status === 401) {
          this.authToken.handleAuthError()
        }
        return Promise.reject(error)
      }
    )
  }

  /**
   * Clean object from cyclic references and proxy objects
   * @param obj The object to clean
   */
  cleanObjectForSerialization(obj: unknown): unknown {
    if (obj === null || typeof obj !== "object") {
      return obj
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      return obj.map((item) => this.cleanObjectForSerialization(item))
    }

    // Handle plain objects - create a clean copy with only serializable properties
    const cleaned: Record<string, unknown> = {}
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = (obj as Record<string, unknown>)[key]
        if (value !== null && typeof value === "object") {
          // Skip circular references and complex objects that might cause issues
          if (value.constructor === Object || Array.isArray(value)) {
            try {
              cleaned[key] = this.cleanObjectForSerialization(value)
            } catch (error) {
              // Skip properties that can't be serialized
              console.warn(`Skipping property ${key} due to serialization error:`, error)
            }
          }
          // Skip other complex objects like Proxy, functions, etc.
        } else {
          cleaned[key] = value
        }
      }
    }
    return cleaned
  }

  /**
   * Build and return the api-url.
   * @param {string} endpoint The endpoint.
   */
  getApiUrl(endpoint: string): string {
    // @ts-expect-error - Vite injects import.meta.env at build time
    return import.meta.env.VITE_API_URL + endpoint
  }

  /**
   * Process a successful result (status code 200).
   * @param res The result.
   */
  processResult<T = unknown>(res: AxiosResponse<T>): ApiResponse<T> {
    // If backend returns an object with success property, use that
    if (res.data && typeof res.data === "object" && "success" in res.data) {
      const responseData = res.data as { success: boolean; data?: T; errors?: ApiError[]; message?: string }
      return {
        success: responseData.success,
        data: (responseData.data || res.data) as T,
        headers: res.headers,
        errors: responseData.errors || [],
        message: responseData.message
      }
    }

    // Handle HTTP 204 No Content as success (common for delete operations)
    if (res.status === 204) {
      return {
        success: true,
        data: null as T,
        headers: res.headers,
        errors: [],
        message: "Operation completed successfully"
      }
    }

    // Fallback to original logic for other APIs - ensure consistent structure
    return {
      success: !!res.data,
      data: res.data,
      headers: res.headers,
      errors: [],
      message: undefined
    }
  }

  /**
   * Process an erroneous result (exception, status code <> 200).
   * @param res The result/error.
   */
  processErrorResult<T = unknown>(res: unknown): ApiResponse<T> {
    let errors: ApiError[] = []
    let errorMessage = "Ein unbekannter Fehler ist aufgetreten"
    let forbiddenData: T | null = null

    if (!res) {
      this.authToken.handleAuthError()
    } else if (typeof res === "string") {
      // Handle string error messages (e.g., from error.message)
      errorMessage = res
      errors = [{ message: res, field: null }]
    } else if (typeof res === "object" && res !== null && "status" in res) {
      // Handle response objects
      const response = res as {
        status: number
        data?: unknown
        headers?: RawAxiosResponseHeaders | AxiosResponseHeaders
      }
      switch (response.status) {
        case 401:
          // Handle authentication errors through secure token management
          this.authToken.handleAuthError()
          break
        case 403: {
          // Not an authentication problem: the server says what is not allowed,
          // and that message belongs in front of the user.
          // Yii puts it in `message` (convertExceptionToArray).
          const forbidden = response.data as { message?: string } | null
          errorMessage = forbidden?.message ?? "Für diese Aktion fehlt Ihnen die Berechtigung"
          errors = [{ message: errorMessage, field: null }]
          // Also handed back under data.error: every caller reads the shape of
          // the 200 answers ({success:false, error}), and without this the
          // message would be swapped for a generic fallback right where it
          // matters.
          forbiddenData = { error: errorMessage } as T
          break
        }
        case 422:
          // validation errors
          errors = Array.isArray(response.data) ? response.data : [response.data as ApiError]
          errorMessage = "Validierungsfehler"
          break
        case 404:
          errorMessage = "Ressource nicht gefunden"
          errors = [{ message: errorMessage, field: null }]
          break
        case 500:
          errorMessage = "Interner Serverfehler"
          errors = [{ message: errorMessage, field: null }]
          break
        default:
          errorMessage = `HTTP-Fehler ${response.status}`
          errors = [{ message: errorMessage, field: null }]
          break
      }

      return {
        success: false,
        data: forbiddenData ?? (null as T),
        headers: response.headers || null,
        errors: errors,
        message: errorMessage
      }
    }

    return {
      success: false,
      data: null as T,
      headers: null,
      errors: errors,
      message: errorMessage
    }
  }

  async makeRequest<T = unknown>(
    method: "get" | "post" | "put" | "patch" | "delete",
    endpoint: string,
    params?: Record<string, unknown>,
    data?: unknown,
    responseType?: string | null,
    useFormData?: boolean,
    customTimeout?: number
  ): Promise<ApiResponse<T>> {
    const url = this.getApiUrl(endpoint)
    const config: AxiosRequestConfig = {
      headers: {},
      responseType: (responseType as AxiosRequestConfig["responseType"]) || "json"
    }

    // M9: Allow custom timeout for specific requests (e.g., long-running operations)
    if (customTimeout !== undefined) {
      config.timeout = customTimeout
    }

    // Check if data is FormData (for file uploads)
    if (data && useFormData) {
      config.headers!["Content-Type"] = "multipart/form-data"
    } else {
      // For object data, send as JSON
      config.headers!["Content-Type"] = "application/json"
    }

    try {
      let requestData: unknown = data

      // For requests with object data, send as JSON (unless it's FormData for file uploads)
      if (data && !useFormData) {
        requestData = JSON.stringify(this.cleanObjectForSerialization(data))
      }

      const requestConfig: AxiosRequestConfig = {
        method,
        url,
        params,
        data: requestData,
        ...config
      }

      const response: AxiosResponse<T> = await axios.request(requestConfig)

      return this.processResult<T>(response)
    } catch (error: unknown) {
      // Handle different types of axios errors properly
      if (typeof error === "object" && error !== null) {
        if ("response" in error && error.response) {
          // Server responded with an error status
          return this.processErrorResult<T>(error.response)
        } else if ("request" in error && error.request) {
          // Request was made but no response received (network error)
          return this.processErrorResult<T>("Netzwerkfehler: Keine Antwort vom Server")
        } else if ("message" in error && typeof error.message === "string") {
          // Something else happened
          return this.processErrorResult<T>(error.message)
        }
      }

      return this.processErrorResult<T>("Unbekannter Fehler")
    }
  }
}
